// Civic pixel loader. This module is bundled by `scripts/build.mjs` into a
// single self-executing browser asset emitted as the stable `pixel.js` URL
// (ticket bryn-pixel-<env>/pixel.js). Customers keep loading
// `https://bryn.civic.com/pixel/pixel.js` — the loader's only job is to
// find its own `<script>`, derive the Civic origin from its `src`, and
// inject a fresh `<script async>` that loads the content-hashed bundle
// (`pixel.<hash>.js`, served `public, max-age=31536000, immutable`).
//
// The hashed filename is substituted in by the build, in place of the
// `__BRYN_PIXEL_BUNDLE__` marker below, before the script is transpiled and
// wrapped in the IIFE the rest of the pixel assets use. A build assertion
// guarantees the inlined name equals `manifest.bundle` so a loader that
// names a missing file cannot ship.
//
// Structural types, no DOM lib: the pixel bundle and loader are imported by
// nothing else in the repo, the bundle is test-stubbed without a real DOM,
// and the loader sits in the same per-module closure as `pixel.ts`.

// `nonce` is the HTMLScriptElement IDL property (not the HTML attribute).
// `getAttribute("nonce")` returns "" on parsed elements because browsers hide
// nonces from the attribute API; `.nonce` is the only reliable read path.
// Modelled as an optional string so a host that doesn't set a nonce (most
// pages) carries nothing across.
export type ScriptLike = {
  getAttribute(name: string): string | null;
  setAttribute(name: string, value: string): void;
  nonce?: string;
};

export type LoaderDocument = {
  // `currentScript` is the loader's own `<script>` element during a parser-
  // inserted or async-dynamic script's top-level run. Null in SSR / a test
  // import, and in Firefox's async-dynamic path (where the bundle falls back
  // to `querySelector`).
  currentScript?: ScriptLike | null;
  // Fallback locator for the loader's own `<script>` when `currentScript` is
  // null. The injected bundle reads its own tag the same way (`findScript`
  // in `pixel.ts`); the duplicated logic here is intentional — see the
  // SYNC NOTE in `findOwnScript`.
  querySelector?: (selector: string) => ScriptLike | null;
  // Synchronous DOM construction: we need to create a `<script>` and append
  // it. Both optional: a host that wires neither is a silent no-op (SSR /
  // test).
  createElement?: (name: string) => ScriptLike | null;
  head?: { appendChild(child: unknown): void } | null;
};

const PIXEL_PATH = "/pixel";
// Derive the Civic origin (scheme + host) from the loader's own `<script src>`.
// A relative src would resolve against the embedding customer page and silently
// load the bundle from the wrong origin. Same regex as `ORIGIN_RE` in pixel.ts,
// duplicated to keep the loader import-free.
const ORIGIN_RE = /^(https?:\/\/[^/]+)/i;

/**
 * Find the loader's own `<script>` element.
 *
 * SYNC NOTE: `pixel.ts` already exports `findScript` with the same logic.
 * We intentionally DUPLICATE the small function here rather than import
 * it: the loader and the bundle are two SEPARATE browser scripts (the
 * loader is shipped as `pixel.js`, the bundle as `pixel.<hash>.js`), and
 * cross-script imports don't survive the IIFE wrapping the build applies.
 * Keeping the logic inline means each script is self-contained at runtime,
 * and the duplication is small enough to maintain in two places. The two
 * selectors MUST stay in sync — `pixel.ts` matches the same prefix below
 * (BRYN-1961).
 */
export const findOwnScript = (doc: LoaderDocument): ScriptLike | null => {
  if (doc.currentScript) return doc.currentScript;
  const query = doc.querySelector;
  if (!query) return null;
  return (
    query.call(doc, "script[data-bryn-pixel-ref]") ??
    query.call(doc, `script[src*="${PIXEL_PATH}/pixel."]`) ??
    null
  );
};

/**
 * Derive the Civic origin (scheme + host) from the loader's own `<script
 * src>`. The script is Civic-hosted, so a relative `src` would resolve
 * against the embedding customer page and silently load the bundle from
 * the wrong origin. Returns null when no absolute `src` is set.
 */
export const deriveOrigin = (script: ScriptLike): string | null =>
  script.getAttribute("src")?.match(ORIGIN_RE)?.[1] ?? null;

/**
 * Build the injected tag's attributes from the loader's own. The `data-bryn-*`
 * attributes Bryn cares about are carried across so the bundle can read them
 * from the INJECTED tag — inside an injected bundle, `document.currentScript`
 * is the injected `<script>`, and if the tag lacks these, the bundle silently
 * misbehaves (BRYN-1961, the trap-to-avoid).
 *
 * `src` is set explicitly: the build substitutes the hashed name into the
 * call to `loadBundle` before transpilation, so the literal passed in by
 * the auto-boot is `pixel.<hash>.js`.
 *
 * The allowlist below is EXHAUSTIVE for `data-bryn-*` attrs — every Bryn
 * data attribute a customer can set on the loader tag is forwarded, so a
 * new attribute (e.g. `data-bryn-personalize` if a future opt-out lands)
 * can't be silently dropped by the loader. The allowlist is named, not a
 * wildcard, because forwarding the customer's own data attrs (`data-foo`)
 * would widen the host's trust to the injected tag in ways the host did
 * not opt into.
 *
 * CSP nonces are forwarded too (Codex review, BRYN-1961): a customer page
 * with `script-src 'nonce-...'` (and no `'strict-dynamic'`) would block the
 * injected bundle — the loader runs on its nonce, but the bundle has none.
 * Sharing the loader's origin does not grant CSP permission; the nonce has
 * to be set on the injected tag explicitly. Read via `.nonce` (the IDL
 * property), not `getAttribute("nonce")`, because browsers hide parsed
 * nonces from the attribute API. Forwarded only when set, so customers
 * without nonce-strict CSPs are unaffected.
 */
export const buildInjectedAttrs = (
  own: ScriptLike,
  origin: string,
  bundleName: string,
): Record<string, string> => {
  const attrs: Record<string, string> = {
    src: `${origin}${PIXEL_PATH}/${bundleName}`,
    async: "",
  };
  // Every Bryn `data-*` attribute a customer may set on the loader tag is
  // forwarded so the bundle can read it from the injected tag. Add new
  // attributes here when they are introduced, not at the call site.
  const ref = own.getAttribute("data-bryn-pixel-ref");
  if (ref != null) attrs["data-bryn-pixel-ref"] = ref;
  const endpoint = own.getAttribute("data-endpoint");
  if (endpoint != null) attrs["data-endpoint"] = endpoint;
  const personalize = own.getAttribute("data-bryn-personalize");
  if (personalize != null) attrs["data-bryn-personalize"] = personalize;
  if (own.nonce) attrs["nonce"] = own.nonce;
  return attrs;
};

const setAttrs = (el: ScriptLike, attrs: Record<string, string>): void => {
  for (const key of Object.keys(attrs)) {
    el.setAttribute(key, attrs[key] ?? "");
  }
};

/**
 * The loader's runtime: locate its own `<script>`, derive the Civic origin,
 * inject an async `<script>` for the content-hashed bundle, and copy the
 * `data-*` attributes across. Wrapped in a try/catch so a throwing host DOM
 * never breaks the page. A no-op when no script is found (SSR / test) or no
 * origin can be derived (relative `src`).
 *
 * The injected bundle reads `window.__brynPixel` for the tag-manager path,
 * which keeps working unchanged: the global is on the page, not on the
 * script tag.
 */
export const loadBundle = (doc: LoaderDocument, bundleName: string): boolean => {
  try {
    const own = findOwnScript(doc);
    if (!own) return false;
    const origin = deriveOrigin(own);
    if (!origin) return false;
    const create = doc.createElement;
    const head = doc.head;
    if (!create || !head) return false;
    const injected = create.call(doc, "script");
    if (!injected) return false;
    setAttrs(injected, buildInjectedAttrs(own, origin, bundleName));
    head.appendChild(injected);
    return true;
  } catch {
    // The whole point of the loader is to never break the host page.
    return false;
  }
};

// The hashed bundle filename is substituted by the build (see
// `scripts/build.mjs`) before transpilation, so the literal below is
// `pixel.<hash>.js` on the wire. The substitution target is the EXACT
// string `__BRYN_PIXEL_BUNDLE__` — keep them in sync.
const BUNDLE_NAME = "__BRYN_PIXEL_BUNDLE__";

try {
  loadBundle((globalThis as unknown as { document?: LoaderDocument }).document ?? {}, BUNDLE_NAME);
} catch {
  // Defence in depth — `loadBundle` already catches internally.
}
